import { useEffect } from 'react';
import { useQuery } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import useModalState from 'shared/hooks/useModalState';
import { useDebounce } from 'shared/hooks/useDebounce';
import { getErrorMessage } from 'shared/utils/errorMessage';
import { showErrorToastWithRetry } from 'shared/utils/showErrorToastWithRetry';
import { getRoleList } from 'features/management/api/getRoleList';
import { managementKeys, managementStaleTime } from '../../api/queryKeys';

/**
 * List query hook for the role management page.
 * Manages URL state (page, pageSize, search), debounced search, and modal
 * state for detail/delete modals. Delete mutation lives in a separate
 * `useDeleteRole` hook (CQRS — query vs command separation). Permission
 * gating lives in the consuming panel via `usePermission(MENU.ROLE_MANAGEMENT)`.
 */
export const useRoleList = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const searchRaw = searchParams.get('search') || '';
  const page = parseInt(searchParams.get('page') || '1', 10);
  const pageSize = parseInt(searchParams.get('pageSize') || '10', 10);

  const debouncedSearch = useDebounce(searchRaw, 500);

  const detailModal = useModalState();
  const deleteModal = useModalState();

  // Set default page params if missing on mount.
  useEffect(() => {
    if (!searchParams.get('page')) {
      setSearchParams({ page: 1, pageSize: 10 });
    }
  }, [searchParams, setSearchParams]);

  const params = { search: debouncedSearch, page, limit: pageSize };

  const { data, isFetching, refetch } = useQuery(
    managementKeys.roleList({ page, pageSize, search: debouncedSearch }),
    () => getRoleList(params),
    {
      staleTime: managementStaleTime.LIST,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        const status = error?.status || error?.response?.status || 500;
        return status >= 500 && failureCount < 3;
      },
      retryDelay: (retryAttempt) => retryAttempt * 3000,
      onError: (error) => {
        const status = error?.status || error?.response?.status || 500;
        const message = getErrorMessage(error);

        if (status >= 500) {
          showErrorToastWithRetry(`List role: ${message}`, () => refetch());
        } else {
          toast.error(`List role: ${message}`);
        }
      },
    }
  );

  const updateSearchParams = (updates) => {
    setSearchParams((prevParams) => {
      const newParams = new URLSearchParams(prevParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value) {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
      });
      return newParams;
    });
  };

  const handlePageSizeChange = (newPageSize) => {
    updateSearchParams({ page: 1, pageSize: newPageSize });
  };

  const handlePaginationChange = (newPage, newPageSize) => {
    updateSearchParams({ page: newPage, pageSize: newPageSize });
  };

  const handleSearchChange = (e) => {
    updateSearchParams({ search: e.target.value, page: 1 });
  };

  return {
    data,
    isFetching,
    refetch,
    page,
    pageSize,
    searchRaw,
    detailModal,
    deleteModal,
    handlePageSizeChange,
    handlePaginationChange,
    handleSearchChange,
  };
};
